import { useEffect, useEffectEvent, useState } from "react";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";

export interface IdleWarningModalProps {
    open: boolean;
    isLoggingOut: boolean;
    getRemainingTime: () => number;
    onStayLoggedIn: () => void;
    onLogoutNow: () => void;
}

const ANNOUNCE_THRESHOLD_SECONDS = 10;
const MINUTE_THRESHOLD_SECONDS = 60;

const toSeconds = (ms: number) => Math.max(0, Math.ceil(ms / 1000));

/** Formats a countdown as "%1 seconds" up to a minute, then as min:seconds beyond that. */
const formatRemainingDuration = (seconds: number) => {
    if (seconds <= MINUTE_THRESHOLD_SECONDS) {
        return translate("%1 seconds", [seconds]);
    }
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${String(secs).padStart(2, "0")}`;
};

/**
 * Warns the user their session is about to expire due to inactivity.
 * The visible countdown updates every second; the aria-live announcement
 * is throttled to every 10 seconds until the final 10-second stretch, to
 * avoid spamming screen readers with a per-second announcement.
 */
export const IdleWarningModal = ({
    open,
    isLoggingOut,
    getRemainingTime,
    onStayLoggedIn,
    onLogoutNow,
}: IdleWarningModalProps) => {
    const [remainingSeconds, setRemainingSeconds] = useState(() => toSeconds(getRemainingTime()));
    const [announcedSeconds, setAnnouncedSeconds] = useState(remainingSeconds);

    const readRemainingTime = useEffectEvent(() => setRemainingSeconds(toSeconds(getRemainingTime())));

    // calculate the remaining time
    useEffect(() => {
        if (!open) {
            return;
        }
        readRemainingTime();
        const interval = setInterval(readRemainingTime, 1000);
        return () => clearInterval(interval);
    }, [open]);

    // calculate the SR-specific remaining time (last 10 seconds only)
    useEffect(() => {
        const shouldAnnounce =
            remainingSeconds <= ANNOUNCE_THRESHOLD_SECONDS
            || Math.abs(announcedSeconds - remainingSeconds) >= ANNOUNCE_THRESHOLD_SECONDS;
        if (shouldAnnounce) {
            setAnnouncedSeconds(remainingSeconds);
        }
    }, [remainingSeconds, announcedSeconds]);

    return (
        <Modal open={open} fullWidth variant="task">
            <ModalTitle>{translate("You've been idle")}</ModalTitle>
            <ModalContent>
                <Typography variant="body1" align="center">
                    {translate(
                        "Your session will expire in %1 due to inactivity.",
                        [formatRemainingDuration(remainingSeconds)]
                    )}
                </Typography>
                <Box component="span" sx={visuallyHidden} aria-live="polite">
                    {translate(
                        "Your session will expire in %1 due to inactivity.",
                        [formatRemainingDuration(announcedSeconds)]
                    )}
                </Box>
            </ModalContent>
            <ModalActions>
                <Button
                    variant={"borderless"}
                    color={"secondary"}
                    onClick={onLogoutNow}
                    loading={isLoggingOut}
                    disabled={isLoggingOut}
                >
                    {translate("Log out now")}
                </Button>
                <Button
                    onClick={(event) => {
                        if (event.isTrusted) {
                            onStayLoggedIn();
                        }
                    }}
                    disabled={isLoggingOut}
                >
                    {translate("Stay logged in")}
                </Button>
            </ModalActions>
        </Modal>
    );
};
